import type { Route } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { roleCanActivate } from 'app/core/auth/guards/role.guard';
import { LayoutComponent, LayoutOption } from 'app/layout/layout.component';
import { initialDataResolver } from './app.resolvers';
import { AuthGuard } from './core/auth/guards/auth.guard';
import { tenantBrandingResolver } from './core/tenant-branding';
import { authFailRoutes } from './modules/auth/auth-fail/auth-fail.routes';
import { unauthorizedRoutes } from './modules/auth/unauthorized/unauthorized.routes';
import { publicRoutes } from './modules/public/public.routes';

// @formatter:off

export const appRoutes: Route[] = [
  // Redirect empty path to '/home'
  { path: '', pathMatch: 'full', redirectTo: 'home' },
  // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  ...authFailRoutes,
  ...unauthorizedRoutes,
  ...publicRoutes,
  { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'home' }, // Auth fail route - accessible without guards
  // Main application routes - protected by MSAL and Auth guards
  {
    path: '',
    canActivate: [MsalGuard, AuthGuard],
    canActivateChild: [MsalGuard, AuthGuard],
    component: LayoutComponent,
    data: {
      layout: LayoutOption.Dense,
    },
    resolve: {
      initialData: initialDataResolver,
      tenantBranding: tenantBrandingResolver,
    },
    children: [
      {
        path: 'home',
        canActivate: [roleCanActivate],
        data: { permission: 'route:home' },
        loadChildren: () => import('app/modules/landing/landing.routes').then((m) => m.landingRoutes),
      },
      {
        path: 'schedule',
        canActivate: [roleCanActivate],
        data: { permission: 'route:schedule' },
        loadChildren: () => import('app/modules/schedule/schedule.routes').then((m) => m.scheduleRoutes),
      },
      {
        path: 'reception',
        canActivate: [roleCanActivate],
        data: { permission: 'route:reception' },
        loadChildren: () => import('app/modules/reception/reception.routes').then((m) => m.receptionRoutes),
      },
      {
        path: 'clinical-data',
        canActivate: [roleCanActivate],
        data: { permission: 'route:clinical-data' },
        loadChildren: () =>
          import('app/modules/admin/clinical-data/clinical-data.routes').then((m) => m.clinicalDataRoutes),
      },
      {
        path: 'patient-surveys',
        canActivate: [roleCanActivate],
        data: { permission: 'route:patient-surveys/manual' },
        loadChildren: () =>
          import('app/modules/admin/patient-surveys/patient-surveys.routes').then((m) => m.patientSurveysRoutes),
      },
      {
        path: 'patients',
        canActivate: [roleCanActivate],
        data: { permission: 'route:patients' },
        loadChildren: () => import('app/modules/admin/patients/patients.routes').then((m) => m.patientsRoutes),
      },
      {
        path: 'posit-connect',
        canActivate: [roleCanActivate],
        data: { permission: 'route:posit-connect' },
        loadChildren: () =>
          import('app/modules/admin/posit-connect/posit-connect.routes').then((m) => m.positConnectRoutes),
      },
      {
        path: 'reports',
        canActivate: [roleCanActivate],
        data: { permission: 'route:reports' },
        loadChildren: () => import('app/modules/admin/reports/reports.routes').then((m) => m.reportsRoutes),
      },
      {
        path: 'interfaces',
        canActivate: [roleCanActivate],
        data: { permission: 'route:interfaces' },
        loadChildren: () => import('app/modules/admin/interfaces/interfaces.routes').then((m) => m.interfacesRoutes),
      },
      {
        path: 'administrative',
        canActivate: [roleCanActivate],
        data: { permission: 'route:administrative/users' },
        loadChildren: () =>
          import('app/modules/admin/administrative/administrative.routes').then((m) => m.administrativeRoutes),
      },
      {
        path: 'profile',
        data: { title: 'Profile' },
        loadChildren: () => import('app/modules/profile/profile.routes').then((m) => m.profileRoutes),
      },
      { path: '**', redirectTo: 'home' },
    ],
  },
];
